import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Typography, Row, Col, Statistic, Spin, message, Table, Tag,
  Progress, Space, Button, Select, Input, Divider, Descriptions, Badge, Radio
} from 'antd';
import {
  ArrowLeftOutlined, DollarOutlined, SwapOutlined, CheckCircleOutlined,
  ClockCircleOutlined, ExclamationCircleOutlined, CloseCircleOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { analysisService } from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const statusConfig = {
  new: { color: 'default', icon: <ClockCircleOutlined />, label: 'New' },
  reviewing: { color: 'processing', icon: <ExclamationCircleOutlined />, label: 'Under Review' },
  planned: { color: 'warning', icon: <ClockCircleOutlined />, label: 'Planned' },
  implemented: { color: 'success', icon: <CheckCircleOutlined />, label: 'Implemented' },
  dismissed: { color: 'error', icon: <CloseCircleOutlined />, label: 'Dismissed' },
};

const priorityConfig = {
  low: { color: 'default', label: 'Low' },
  medium: { color: 'blue', label: 'Medium' },
  high: { color: 'orange', label: 'High' },
  critical: { color: 'red', label: 'Critical' },
};

const ConsolidationDetail = () => {
  const { vendor1Id, vendor2Id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [consolidation, setConsolidation] = useState(null);
  const [products, setProducts] = useState(null);
  const [formData, setFormData] = useState({
    status: 'new',
    priority: 'medium',
    keep_vendor_id: null,
    notes: '',
    decision_reason: '',
    actual_savings: null,
  });

  useEffect(() => {
    fetchData();
  }, [vendor1Id, vendor2Id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [consolidationData, productData] = await Promise.all([
        analysisService.getConsolidation(vendor1Id, vendor2Id),
        analysisService.getVendorPairProducts(vendor1Id, vendor2Id),
      ]);
      setConsolidation(consolidationData);
      setProducts(productData);
      setFormData({
        status: consolidationData.status || 'new',
        priority: consolidationData.priority || 'medium',
        keep_vendor_id: consolidationData.keep_vendor_id,
        notes: consolidationData.notes || '',
        decision_reason: consolidationData.decision_reason || '',
        actual_savings: consolidationData.actual_savings,
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Failed to load consolidation details');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await analysisService.updateConsolidation(vendor1Id, vendor2Id, formData);
      setConsolidation(updated);
      message.success('Changes saved successfully');
    } catch (error) {
      console.error('Error saving:', error);
      message.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading consolidation details...</div>
      </div>
    );
  }

  if (!consolidation || !products) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Text type="danger">Failed to load consolidation details</Text>
        <br />
        <Button onClick={() => navigate('/recommendations')} style={{ marginTop: 16 }}>
          Back to Consolidation List
        </Button>
      </div>
    );
  }

  const productColumns = [
    {
      title: `${products.vendor1.name} Product`,
      dataIndex: ['product1', 'name'],
      key: 'product1',
      render: (name, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          {row.product1.category && (
            <Text type="secondary" style={{ fontSize: 12 }}>{row.product1.category}</Text>
          )}
          {row.product1.description && (
            <Paragraph ellipsis={{ rows: 2 }} style={{ fontSize: 12, marginBottom: 0, color: '#666' }}>
              {row.product1.description}
            </Paragraph>
          )}
        </Space>
      ),
    },
    {
      title: `${products.vendor2.name} Product`,
      dataIndex: ['product2', 'name'],
      key: 'product2',
      render: (name, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          {row.product2.category && (
            <Text type="secondary" style={{ fontSize: 12 }}>{row.product2.category}</Text>
          )}
          {row.product2.description && (
            <Paragraph ellipsis={{ rows: 2 }} style={{ fontSize: 12, marginBottom: 0, color: '#666' }}>
              {row.product2.description}
            </Paragraph>
          )}
        </Space>
      ),
    },
    {
      title: 'Similarity',
      dataIndex: 'similarity',
      key: 'similarity',
      width: 150,
      sorter: (a, b) => a.similarity - b.similarity,
      render: (similarity) => (
        <Progress
          percent={Math.round(similarity * 100)}
          size="small"
          status={similarity > 0.85 ? "success" : similarity > 0.75 ? "normal" : "exception"}
          format={percent => `${percent}%`}
        />
      ),
    },
  ];

  const statusInfo = statusConfig[formData.status] || statusConfig.new;
  const priorityInfo = priorityConfig[formData.priority] || priorityConfig.medium;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/recommendations')}
          style={{ marginBottom: 16 }}
        >
          Back to Consolidation List
        </Button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title level={2} style={{ marginBottom: 8 }}>
              <Space>
                <Tag color="blue" style={{ fontSize: 16, padding: '4px 12px' }}>{consolidation.vendor1.name}</Tag>
                <SwapOutlined />
                <Tag color="blue" style={{ fontSize: 16, padding: '4px 12px' }}>{consolidation.vendor2.name}</Tag>
              </Space>
            </Title>
            <Space>
              <Badge status={statusInfo.color} text={statusInfo.label} />
              <Divider type="vertical" />
              <Tag color={priorityInfo.color}>{priorityInfo.label} Priority</Tag>
            </Space>
          </div>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            size="large"
          >
            Save Changes
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={`${consolidation.vendor1.name} Spend`}
              value={consolidation.vendor1.annual_spend}
              prefix={<DollarOutlined />}
              precision={0}
              valueStyle={{ color: '#1890ff' }}
              formatter={(value) => `$${value.toLocaleString()}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={`${consolidation.vendor2.name} Spend`}
              value={consolidation.vendor2.annual_spend}
              prefix={<DollarOutlined />}
              precision={0}
              valueStyle={{ color: '#1890ff' }}
              formatter={(value) => `$${value.toLocaleString()}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Estimated Savings"
              value={consolidation.estimated_savings || 0}
              prefix={<DollarOutlined />}
              precision={0}
              valueStyle={{ color: '#52c41a' }}
              formatter={(value) => `$${value.toLocaleString()}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Product Overlaps"
              value={products.total_overlaps}
              prefix={<SwapOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Management Panel */}
      <Row gutter={24}>
        <Col xs={24} lg={8}>
          <Card title="Opportunity Management" style={{ marginBottom: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Status</Text>
                <Select
                  value={formData.status}
                  onChange={(value) => handleFormChange('status', value)}
                  style={{ width: '100%' }}
                >
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <Option key={key} value={key}>
                      <Space>
                        {config.icon}
                        {config.label}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </div>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Priority</Text>
                <Select
                  value={formData.priority}
                  onChange={(value) => handleFormChange('priority', value)}
                  style={{ width: '100%' }}
                >
                  {Object.entries(priorityConfig).map(([key, config]) => (
                    <Option key={key} value={key}>
                      <Tag color={config.color}>{config.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </div>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Vendor to Keep</Text>
                <Radio.Group
                  value={formData.keep_vendor_id}
                  onChange={(e) => handleFormChange('keep_vendor_id', e.target.value)}
                  style={{ width: '100%' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Radio value={consolidation.vendor1.id}>
                      <Space>
                        {consolidation.vendor1.name}
                        <Text type="secondary">(${consolidation.vendor1.annual_spend.toLocaleString()})</Text>
                        {formData.keep_vendor_id === consolidation.vendor1.id && (
                          <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        )}
                      </Space>
                    </Radio>
                    <Radio value={consolidation.vendor2.id}>
                      <Space>
                        {consolidation.vendor2.name}
                        <Text type="secondary">(${consolidation.vendor2.annual_spend.toLocaleString()})</Text>
                        {formData.keep_vendor_id === consolidation.vendor2.id && (
                          <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        )}
                      </Space>
                    </Radio>
                  </Space>
                </Radio.Group>
              </div>

              {formData.status === 'implemented' && (
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>Actual Savings Realized</Text>
                  <Input
                    prefix="$"
                    type="number"
                    value={formData.actual_savings}
                    onChange={(e) => handleFormChange('actual_savings', parseFloat(e.target.value) || null)}
                    placeholder="Enter actual savings"
                  />
                </div>
              )}

              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Decision Reason</Text>
                <TextArea
                  value={formData.decision_reason}
                  onChange={(e) => handleFormChange('decision_reason', e.target.value)}
                  placeholder="Why was this decision made?"
                  rows={3}
                />
              </div>

              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Notes</Text>
                <TextArea
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  placeholder="Additional notes..."
                  rows={4}
                />
              </div>
            </Space>
          </Card>

          {/* Timestamps */}
          {(consolidation.reviewed_at || consolidation.implemented_at) && (
            <Card title="Activity" size="small">
              <Descriptions column={1} size="small">
                {consolidation.reviewed_at && (
                  <Descriptions.Item label="Reviewed">
                    {new Date(consolidation.reviewed_at).toLocaleString()}
                  </Descriptions.Item>
                )}
                {consolidation.implemented_at && (
                  <Descriptions.Item label="Implemented">
                    {new Date(consolidation.implemented_at).toLocaleString()}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Created">
                  {new Date(consolidation.created_at).toLocaleString()}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}
        </Col>

        {/* Product Overlaps */}
        <Col xs={24} lg={16}>
          <Card
            title={`Overlapping Products (${products.total_overlaps})`}
            extra={
              <Text type="secondary">
                Showing products with {'>'}70% similarity
              </Text>
            }
          >
            <Table
              columns={productColumns}
              dataSource={products.product_pairs}
              rowKey={(row) => `${row.product1.id}-${row.product2.id}`}
              pagination={{ pageSize: 20 }}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ConsolidationDetail;
